import { useState, useEffect, useRef } from 'react';

const NEXT_STEPS = [
  {
    icon: 'ri-mail-send-line',
    title: 'We receive your request',
    desc: 'Your message lands directly with our team — no bots, no ticketing system.',
  },
  {
    icon: 'ri-time-line',
    title: 'Reply within 1 business day',
    desc: 'We\'ll send a tailored pricing proposal and answer any questions by email.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Optional discovery call',
    desc: 'If helpful, we can schedule a short call to align on team size and delivery format.',
  },
];

export default function StickyEnrolCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const hasHighlightedRef = useRef(false);
  const intersectingRef = useRef<Set<string>>(new Set());
  const barRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep CSS custom property --sticky-footer-h updated
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const update = () => {
      const h = bar.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--sticky-footer-h', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // Intersection observer for trigger sections
  useEffect(() => {
    const sectionIds = ['organisational-outcomes', 'pricing-section'];
    const updateVisibility = () => {
      const nowVisible = intersectingRef.current.size > 0;
      setIsVisible(nowVisible);

      // Trigger highlight the first time pricing-section enters view
      if (
        nowVisible &&
        intersectingRef.current.has('pricing-section') &&
        !hasHighlightedRef.current
      ) {
        hasHighlightedRef.current = true;
        // Small delay so the slide-in animation finishes first
        setTimeout(() => {
          setHighlight(true);
          setTimeout(() => setHighlight(false), 1400);
        }, 600);
      }
    };
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              intersectingRef.current.add(id);
            } else {
              intersectingRef.current.delete(id);
            }
            updateVisibility();
          });
        },
        { root: null, threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToContact = () => {
    setShowTooltip(false);
    const el = document.getElementById('contact-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBtnMouseEnter = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setShowTooltip(true);
  };

  const handleBtnMouseLeave = () => {
    hideTimerRef.current = setTimeout(() => setShowTooltip(false), 200);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const handleTooltipMouseLeave = () => {
    hideTimerRef.current = setTimeout(() => setShowTooltip(false), 200);
  };

  const handleBtnTap = (e: React.MouseEvent) => {
    // On touch devices toggle the tooltip; on desktop it navigates
    const isTouchDevice = window.matchMedia('(hover: none)').matches;
    if (isTouchDevice) {
      e.preventDefault();
      setShowTooltip((prev) => !prev);
    } else {
      scrollToContact();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        transition: isVisible
          ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1)'
          : 'transform 0.38s cubic-bezier(0.55, 0, 1, 0.45), opacity 0.3s ease-in',
        transform: isVisible ? 'translateY(0)' : 'translateY(110%)',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      {/* Gradient fade above the bar */}
      <div className="h-8 bg-gradient-to-t from-[#0E0503]/20 to-transparent pointer-events-none" />

      <div
        ref={barRef}
        className="bg-[#0E0503] border-t border-[#BA3D03]/40 shadow-2xl relative overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Highlight glow overlay */}
        <div
          style={{
            transition: highlight
              ? 'opacity 0.15s ease-in'
              : 'opacity 0.9s ease-out',
            opacity: highlight ? 1 : 0,
            pointerEvents: 'none',
          }}
          className="absolute inset-0 bg-[#E48523]/18 z-0"
        />
        {/* Highlight border pulse */}
        <div
          style={{
            transition: highlight
              ? 'opacity 0.15s ease-in, box-shadow 0.15s ease-in'
              : 'opacity 0.9s ease-out, box-shadow 0.9s ease-out',
            opacity: highlight ? 1 : 0,
            boxShadow: highlight
              ? '0 -4px 32px 0 rgba(228,133,35,0.55), 0 0 0 2px rgba(228,133,35,0.45)'
              : '0 0 0 0 rgba(228,133,35,0)',
            pointerEvents: 'none',
          }}
          className="absolute inset-0 z-0 rounded-none"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: message */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className="ri-mail-line text-[#E48523] text-xl"></i>
            </div>
            <p className="text-white text-sm font-medium leading-snug">
              Questions about access or pricing?
              <span className="hidden sm:inline text-[#E8C580]/70 font-normal ml-1">
                Contact us — we&apos;ll reply by email.
              </span>
            </p>
          </div>

          {/* Right: CTAs */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0 w-full sm:w-auto">
            {/* Request Company Pricing button + tooltip wrapper */}
            <div className="relative w-full sm:w-auto">
              {/* "What happens next" mini-modal */}
              <div
                ref={tooltipRef}
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
                style={{
                  transition: 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.22,1,0.36,1)',
                  opacity: showTooltip ? 1 : 0,
                  transform: showTooltip ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.97)',
                  pointerEvents: showTooltip ? 'auto' : 'none',
                }}
                className="absolute bottom-[calc(100%+12px)] right-0 left-0 sm:left-auto w-auto sm:w-72 bg-[#1A0A05] border border-[#BA3D03]/40 rounded-xl shadow-2xl p-4 z-50"
              >
                {/* Arrow */}
                <div
                  className="absolute -bottom-[7px] right-6 w-3 h-3 bg-[#1A0A05] border-r border-b border-[#BA3D03]/40 rotate-45"
                />

                <p className="text-[#E8C580] text-xs font-bold uppercase tracking-widest mb-3">
                  What happens next
                </p>

                <ol className="flex flex-col gap-3">
                  {NEXT_STEPS.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-[#BA3D03]/20 mt-0.5">
                        <i className={`${step.icon} text-[#E48523] text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold leading-snug">{step.title}</p>
                        <p className="text-[#E8C580]/60 text-xs leading-snug mt-0.5">{step.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                <button
                  onClick={scrollToContact}
                  className="mt-4 w-full py-2 bg-[#BA3D03] hover:bg-[#9A3203] text-white text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Let&apos;s get started →
                </button>
              </div>

              <button
                ref={btnRef}
                onClick={handleBtnTap}
                onMouseEnter={handleBtnMouseEnter}
                onMouseLeave={handleBtnMouseLeave}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#BA3D03] text-white rounded-lg text-sm font-bold hover:bg-[#9A3203] transition-all whitespace-nowrap cursor-pointer shadow-md flex items-center justify-center gap-2"
              >
                <i className="ri-building-line text-base w-4 h-4 flex items-center justify-center"></i>
                Request Company Pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
